// The Make plan's timeline per project (ERP-PLAN §5.4): one row per make item, with its builds (open make POs
// solid, the plan's next build faded) from start to finished at HHL, parts landing at the CM (on a PO: filled;
// still to order: hollow) and the day the finished goods reach HHL. Days from today along the bottom; hover or
// focus any mark for its details (the Builds and Parts to send tabs list every value too).
import { type ReactNode, useState } from "react";
import type { ProjectTimeline, TimelineEvent } from "../../../../shared/make.ts";
import { addPlanDays, shortDate } from "../../../../shared/planning.ts";
import { setSearch } from "../../lib/router.tsx";
import { cx } from "../../ui/controls.tsx";

const ROW = 36;
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function Legend() {
  const item = (mark: ReactNode, label: string) => (
    <span className="flex items-center gap-1.5">
      {mark}
      {label}
    </span>
  );
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted">
      {item(<span className="inline-block h-2.5 w-5 rounded-[4px] bg-[var(--series-1)]" />, "Build on a make PO")}
      {item(<span className="inline-block h-2.5 w-5 rounded-[4px] bg-[var(--series-1)] opacity-35" />, "Next build (planned)")}
      {item(<span className="inline-block size-2.5 rounded-full bg-[var(--series-2)]" />, "Parts land at the CM (PO)")}
      {item(<span className="inline-block size-2.5 rounded-full border-2 border-[var(--series-2)]" />, "Parts still to order")}
      {item(<span className="inline-block size-2.5 rotate-45 bg-[var(--series-3)]" />, "Finished (at HHL, or the CM that uses it)")}
    </div>
  );
}

export function MakeTimeline({ timeline, today }: { timeline: ProjectTimeline; today: string }) {
  const [tip, setTip] = useState<{ text: string; left: number; top: number } | null>(null);
  // At least three months, at most a year, a week past the last thing on it.
  const to = Math.min(365, Math.max(90, Math.ceil((timeline.to + 7) / 7) * 7));
  const x = (d: number) => `${(Math.min(to, Math.max(0, d)) / to) * 100}%`;
  const date = (d: number) => shortDate(addPlanDays(today, d), today);
  // Month starts along the axis.
  const ticks: { d: number; label: string }[] = [];
  for (let d = 1; d <= to; d++) {
    const iso = addPlanDays(today, d);
    if (iso.endsWith("-01")) ticks.push({ d, label: `${MONTHS[Number(iso.slice(5, 7)) - 1]}${iso.slice(5, 7) === "01" ? ` ${iso.slice(0, 4)}` : ""}` });
  }
  const show = (e: TimelineEvent, row: number) => {
    const when = e.kind === "build" ? `${date(e.from)} → ${date(e.to)}` : date(e.day);
    setTip({
      text: `${e.label} · ${when}${e.planned && e.kind === "build" ? " (planned)" : ""}`,
      left: (Math.min(to, Math.max(0, e.kind === "build" ? (e.from + e.to) / 2 : e.day)) / to) * 100,
      top: row * ROW,
    });
  };
  const hide = () => setTip(null);
  return (
    <section className="border-b border-line px-4 py-3">
      <div className="mb-2 flex items-center gap-3">
        <h2 className="font-semibold">{timeline.name}</h2>
        <span className="text-[12px] text-muted">
          {timeline.rows.length} make item{timeline.rows.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="flex">
        <div className="w-56 shrink-0">
          {timeline.rows.map(({ row }) => (
            <button
              key={row.item.id}
              type="button"
              onClick={() => setSearch({ item: row.item.id, tab: null })}
              className="flex w-full flex-col justify-center truncate pr-3 text-left hover:text-accent-text"
              style={{ height: ROW }}
            >
              <span className="truncate font-mono text-[12px] font-medium">{row.item.sku}</span>
              <span className="truncate text-[11px] text-muted">{row.vendor ? row.vendor.short_name || row.vendor.name : ""}</span>
            </button>
          ))}
        </div>
        <div className="relative min-w-0 flex-1" onPointerLeave={hide}>
          {/* Month gridlines (hairline, recessive) and today. */}
          {ticks.map((t) => (
            <div key={t.d} className="absolute top-0 border-l border-line" style={{ left: x(t.d), height: timeline.rows.length * ROW }} />
          ))}
          <div className="absolute top-0 border-l border-line-strong" style={{ left: 0, height: timeline.rows.length * ROW }} />
          {timeline.rows.map(({ row, events }, i) => (
            <div key={row.item.id} className="absolute inset-x-0" style={{ top: i * ROW, height: ROW }}>
              {events.map((e, k) => {
                const key = `${e.kind}-${k}`;
                const handlers = { onPointerEnter: () => show(e, i), onFocus: () => show(e, i), onBlur: hide, tabIndex: 0, "aria-label": e.label };
                if (e.kind === "build") {
                  const width = `max(4px, calc(${x(e.to)} - ${x(e.from)}))`;
                  return (
                    <div key={key} {...handlers} className="absolute top-1/2 h-6 -translate-y-1/2 outline-none" style={{ left: x(e.from), width }}>
                      <div className={cx("mt-[6px] h-3 rounded-[4px] bg-[var(--series-1)]", e.planned && "opacity-35")} />
                    </div>
                  );
                }
                const shape =
                  e.kind === "ship" ? (
                    <span className="block size-2.5 rotate-45 bg-[var(--series-3)] ring-2 ring-[var(--surface)]" />
                  ) : e.planned ? (
                    <span className="block size-2.5 rounded-full border-2 border-[var(--series-2)] bg-[var(--surface)]" />
                  ) : (
                    <span className="block size-2.5 rounded-full bg-[var(--series-2)] ring-2 ring-[var(--surface)]" />
                  );
                // A 24px hit area around a 10px mark.
                return (
                  <div
                    key={key}
                    {...handlers}
                    className="absolute top-1/2 flex size-6 -translate-x-1/2 -translate-y-1/2 items-center justify-center outline-none"
                    style={{ left: x(e.day) }}
                  >
                    {shape}
                  </div>
                );
              })}
            </div>
          ))}
          <div style={{ height: timeline.rows.length * ROW }} />
          <div className="relative h-5 text-[10px] text-faint tabular-nums">
            <span className="absolute left-0">Today</span>
            {/* A month label too close to "Today" would sit on it. */}
            {ticks
              .filter((t) => t.d / to > 0.07)
              .map((t) => (
                <span key={t.d} className="absolute -translate-x-1/2" style={{ left: x(t.d) }}>
                  {t.label}
                </span>
              ))}
          </div>
          {tip && (
            <div
              className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-md border border-line bg-surface px-2 py-1 text-[11px] whitespace-nowrap shadow-pop"
              style={{ left: `${Math.min(90, Math.max(10, tip.left))}%`, top: tip.top + 4 }}
            >
              {tip.text}
            </div>
          )}
        </div>
      </div>
      <div className="mt-2 pl-56">
        <Legend />
      </div>
    </section>
  );
}
